import React from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useReadingList, type BookmarkInput } from "@/hooks/useReadingList";
import { toast } from "sonner";

interface BookmarkButtonProps extends BookmarkInput {
  className?: string;
  variant?: "solid" | "ghost";
}

/**
 * Save-for-later bookmark button.
 * - Signed-in users toggle the bookmark against `reading_bookmarks`.
 * - Signed-out users see a link to sign in, with the slug preserved as a return path.
 */
const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  slug,
  title,
  category,
  hero_image,
  kind = "article",
  className = "",
  variant = "ghost",
}) => {
  const { user } = useAuth();
  const { isSaved, toggle, loading } = useReadingList();

  const base =
    "inline-flex items-center gap-2 rounded-full text-[13px] font-medium h-11 px-4 transition-colors active-press disabled:opacity-60";
  const styles =
    variant === "solid"
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "border border-border/70 bg-background/60 hover:bg-muted";

  if (!user) {
    const returnTo = `/${slug.replace(/^\//, "")}`;
    return (
      <Link
        to={`/signup?next=${encodeURIComponent(returnTo)}`}
        className={`${base} ${styles} ${className}`}
        aria-label="Sign in to save this article"
      >
        <Bookmark className="h-4 w-4" aria-hidden />
        Save for later
      </Link>
    );
  }

  const saved = isSaved(slug);

  const handleClick = async () => {
    const nowSaved = await toggle({ slug, title, category, hero_image, kind });
    toast.success(nowSaved ? "Saved to your reading list" : "Removed from reading list");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      aria-pressed={saved}
      className={`${base} ${styles} ${className}`}
    >
      {saved ? (
        <>
          <BookmarkCheck className="h-4 w-4" aria-hidden />
          Saved
        </>
      ) : (
        <>
          <Bookmark className="h-4 w-4" aria-hidden />
          Save for later
        </>
      )}
    </button>
  );
};

export default BookmarkButton;
